import React, { useState, useEffect, useMemo } from 'react';
import { EditOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { Col, Row, Spin, Typography, Table, Space, Descriptions, Button, Popover, message } from 'antd';
import { Formik } from 'formik';
import { Form } from 'formik-antd';
import { formatPrice, formatPercent } from '~/Utils';
import moment from 'moment';
import { createPayAppsClient, updatePayAppsClient } from '~/services/hooks/payAppsClients';
import FormItem from './formItem';
// import testData from './test.json';

const { Text } = Typography;

export default function PayAppClient({
  selectedRecord,
  project,
  proposalClient,
  visible,
  viewOnly,
  onClose,
  onUpdateItem,
  onInsertItem,
  t,
  setPayAppVisible,
}) {
  // console.log('PayAppClient component rendered');
  // console.log('selectedRecord:', selectedRecord);
  // console.log('project:', project);
  // console.log('proposalClient:', proposalClient);

  // Use test data for development purposes
  // const selectedRecord = testData.selectedRecord || null;
  // const proposalClient = testData.proposalClient || null;
  // const visible = testData.visible || false;
  // const viewOnly = testData.viewOnly || false;
  // const onClose = testData.onClose || (() => {});
  // const onUpdateItem = testData.onUpdateItem || (() => {});
  // const onInsertItem = testData.onInsertItem || (() => {});

  const initialValues = {
    id: null,
    project: project,
    applDate: moment().format('YYYY-MM-DD HH:mm:ss'),
    periodTo: moment().format('YYYY-MM-DD HH:mm:ss'),
    costCode: 'no info',
    contractFor: 'no info',
    architectsProjectNumber: 0,
    signatureDate: proposalClient?.proposalSignature?.date,
    total: 0.0,
    items: [],
  };

  const recordData = selectedRecord || initialValues;
  const [loading, setLoading] = useState(false);
  const [totalScheduleValue, setTotalScheduleValue] = useState(0);
  const [totalPreviousApplication, setTotalPreviousApplication] = useState(0);
  const [totalWorkInPlace, setTotalWorkInPlace] = useState(0);
  const [totalStoredMaterials, setTotalStoredMaterials] = useState(0);
  const [totalToDate, setTotalToDate] = useState(0);
  const [totalBalanceToFinish, setTotalBalanceToFinish] = useState(0);
  const [totalPercentageComplete, setTotalPercentageComplete] = useState(0);
  const [totalRetainage, setTotalRetainage] = useState(0);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const orderItems = items => {
    if (!items) return [];
    const items1 = items.filter(
      // PROPOSAL CLIENT ITEM
      item =>
        item.proposalClientItem !== null &&
        // item.proposalOtherItem === null &&
        item.generalConditions === false &&
        item.overheadAndProfit === false
      // item.changeOrderClient === null
    );
    const items2 = items.filter(
      // PROPOSAL OTHER ITEM
      item => item.proposalOtherItem !== null && item.generalConditions === false && item.overheadAndProfit === false
    );
    const items3 = items.filter(
      // GENERAL CONDITIONS
      item => item.proposalOtherItem === null && item.generalConditions === true && item.overheadAndProfit === false
    );
    const items4 = items.filter(
      // OVERHEAD AND PROFIT
      item => item.proposalOtherItem === null && item.generalConditions === false && item.overheadAndProfit === true
    );
    const items5 = items.filter(
      // CHANGE ORDER CLIENT
      item => item.generalConditions === false && item.overheadAndProfit === false && item.changeOrderClient !== null
    );
    const orderedItems = [...items1, ...items2, ...items3, ...items4, ...items5];
    return orderedItems;
  };

  const removeValueInEspecialItems = values => {
    values.forEach(item => {
      if (item.generalConditions === true || item.overheadAndProfit === true) {
        item.proposalClientItem = null;
      }
    });
  };

  // // recordData.items = orderItems(recordData?.items);
  // const orderedItems = orderItems(recordData?.items || []);
  // // cria um novo objeto de inicialValues para o Formik
  // const initialFormValues = {
  //   ...recordData,
  //   items: orderedItems,
  // };

 // memoiza a ordenação dos items
 const orderedItems = useMemo(
   () => orderItems(recordData.items || []),
   [recordData.items]
 );
 // memoiza o objeto inicial do Formik
 const initialFormValues = useMemo(
   () => ({ ...recordData, items: orderedItems }),
   [recordData, orderedItems]
 );

  const calculateTotals = valuesItems => {
    let totScheduleValue = 0;
    let totPreviousApplication = 0;
    let totWorkInPlace = 0;
    let totStoredMaterials = 0;
    let totToDate = 0;
    let totPercentageComplete = 0;
    let totBalanceToFinish = 0;
    let totRetainage = 0;

    valuesItems.forEach(item => {
      totScheduleValue +=
        item.proposalClientItem !== null
          ? item.proposalClientItem.total
          : item.proposalOtherItem !== null
          ? item.proposalOtherItem.value
          : item.changeOrderClient !== null
          ? item.changeOrderClient?.priceInfo?.items?.reduce((a, b) => a + b.unitCost * b.quantity, 0)
          : 0;

      totPreviousApplication += item.previousApplications;
      totWorkInPlace += item.workInPlace;
      totStoredMaterials += item.storedMaterials;
    });

    totToDate = totPreviousApplication + totWorkInPlace + totStoredMaterials;
    totRetainage = totToDate * 0.1;
    totBalanceToFinish = totScheduleValue - totalToDate;
    totPercentageComplete = totalToDate / totScheduleValue;

    // set values to state
    setTotalScheduleValue(totScheduleValue);
    setTotalPreviousApplication(totPreviousApplication);
    setTotalWorkInPlace(totWorkInPlace);
    setTotalStoredMaterials(totStoredMaterials);
    setTotalToDate(totToDate);
    setTotalRetainage(totRetainage);
    setTotalBalanceToFinish(totBalanceToFinish);
    setTotalPercentageComplete(totPercentageComplete);
  };

  // dispara o cálculo sempre que items mudar, evitando chamar dentro do render
  // useEffect(() => {
  //   calculateTotals(values.items);
  // }, [values.items]);
  const TotalsCalculator = ({ items }) => {
    useEffect(() => {
      calculateTotals(items);
    }, [items]);
    return null;
  };

  const handleSave = async values => {
    console.log('handleSave called with values:', values);
    // verify if values.items is an array and has items and if total value is greater than 0
    if (!Array.isArray(values.items) || values.items.length === 0) {
      console.log('No items in values.items');
      message.error(t('screens:payAppClients.items.noItems'));
      return;
    }
    if (!values.items.some(item => item.workInPlace > 0 || item.storedMaterials > 0)) {
      console.log('Work in place or stored materials are required');
      message.error(t('screens:payAppClients.items.workInPlaceOrStoredMaterialsRequired'));
      return;
    }
    if (values.items.some(item => item.proposalClientItem === null && item.proposalOtherItem === null)) {
      console.log('No proposal item selected');
      message.error(t('screens:payAppClients.items.selectProposal'));
      return;
    }

    setLoading(true);
    if (values.items) removeValueInEspecialItems(values.items);
    values.total = totalWorkInPlace * 0.9;
    values.invoice = {
      id: null,
      date: moment().format('YYYY-MM-DD HH:mm:ss'),
      paymentDate: null,
      attachments: [],
    };
    if (values.id !== null) {
      const data = await updatePayAppsClient(values);
      onUpdateItem(data);
    } else {
      values.items.forEach(item => {
        item.id = null;
      });
      const data = await createPayAppsClient(values);
      onInsertItem(data);
    }
    setPayAppVisible(false);
    onClose();
    setLoading(false);
  };

  const handleEdit = record => {
    setSelectedItem(record);
    setShowModal(true);
  };

  const tableColumns = [
    {
      title: t('screens:payAppClients.items.itemId'),
      dataIndex: 'id',
      key: 'id',
      ellipsis: { showTitle: false },
      width: '50px',
      align: 'center',
    },
    {
      title: t('screens:payAppClients.items.description'),
      dataIndex: 'proposalClientItem',
      key: 'proposalClientItem.description',
      ellipsis: { showTitle: false },
      align: 'center',
      width: '250px',
      render: proposalClientItem => <>{proposalClientItem?.description}</>,
    },
    {
      title: t('screens:payAppClients.items.scheduledValue'),
      dataIndex: 'proposalClientItem',
      key: 'proposalClientItem.total',
      ellipsis: { showTitle: false },
      align: 'center',
      render: proposalClientItem => <>{formatPrice(proposalClientItem?.total)}</>,
    },
    {
      title: t('screens:payAppClients.items.previousApplications'),
      dataIndex: 'previousApplications',
      key: 'previousApplications',
      ellipsis: { showTitle: false },
      align: 'center',
      render: previousApplications => <>{formatPrice(previousApplications)}</>,
    },
    {
      title: t('screens:payAppClients.items.workInPlace'),
      dataIndex: 'workInPlace',
      key: 'workInPlace',
      ellipsis: { showTitle: false },
      align: 'center',
      render: workInPlace => <>{formatPrice(workInPlace)}</>,
    },
    {
      title: t('screens:payAppClients.items.storedMaterials'),
      dataIndex: 'storedMaterials',
      key: 'storedMaterials',
      ellipsis: { showTitle: false },
      align: 'center',
      render: storedMaterials => <>{formatPrice(storedMaterials)}</>,
    },
    {
      title: t('screens:payAppClients.items.totalToDate'),
      key: 'totalToDate',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => <>{formatPrice(record.previousApplications + record.workInPlace + record.storedMaterials)}</>,
    },
    {
      title: t('screens:payAppClients.items.percentage'),
      key: 'percentage',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>
          {formatPercent(
            record.proposalClientItem?.total > 0
              ? (record.previousApplications + record.workInPlace + record.storedMaterials) /
                  record.proposalClientItem.total
              : 0
          )}
        </>
      ),
    },
    {
      title: t('screens:payAppClients.items.balanceToFinish'),
      key: 'balanceToFinish',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>
          {console.log(
            'Balance to finish:',
            record.proposalClientItem?.total,
            record.previousApplications,
            record.workInPlace,
            record.storedMaterials
          )}
          {formatPrice(
            record.proposalClientItem?.total -
              (record.previousApplications + record.workInPlace + record.storedMaterials)
          )}
        </>
      ),
    },
    {
      title: t('screens:payAppClients.items.retainage'),
      key: 'retainage',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>{formatPrice((record.previousApplications + record.workInPlace + record.storedMaterials) * 0.1)}</>
      ),
    },
    {
      title: t('screens:estimate.actions'),
      key: 'actions',
      align: 'center',
      width: '70px',
      render: record => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title={t('screens:estimate.editItem')}
          />
        </Space>
      ),
    },
  ];

  const tableColumnsOtherItems = [
    {
      title: t('screens:payAppClients.items.itemId'),
      dataIndex: 'id',
      key: 'id',
      ellipsis: { showTitle: false },
      width: '50px',
      align: 'center',
    },
    {
      title: t('screens:payAppClients.items.description'),
      dataIndex: 'proposalOtherItem',
      key: 'proposalOtherItem.description',
      ellipsis: { showTitle: false },
      align: 'center',
      width: '250px',
      render: proposalOtherItem => <>{proposalOtherItem?.description}</>,
    },
    {
      title: t('screens:payAppClients.items.scheduledValue'),
      dataIndex: 'proposalOtherItem',
      key: 'proposalOtherItem.value',
      ellipsis: { showTitle: false },
      align: 'center',
      render: proposalOtherItem => <>{formatPrice(proposalOtherItem?.value)}</>,
    },
    {
      title: t('screens:payAppClients.items.previousApplications'),
      dataIndex: 'previousApplications',
      key: 'previousApplications',
      ellipsis: { showTitle: false },
      align: 'center',
      render: previousApplications => <>{formatPrice(previousApplications)}</>,
    },
    {
      title: t('screens:payAppClients.items.workInPlace'),
      dataIndex: 'workInPlace',
      key: 'workInPlace',
      ellipsis: { showTitle: false },
      align: 'center',
      render: workInPlace => <>{formatPrice(workInPlace)}</>,
    },
    {
      title: t('screens:payAppClients.items.storedMaterials'),
      dataIndex: 'storedMaterials',
      key: 'storedMaterials',
      ellipsis: { showTitle: false },
      align: 'center',
      render: storedMaterials => <>{formatPrice(storedMaterials)}</>,
    },
    {
      title: t('screens:payAppClients.items.totalToDate'),
      key: 'totalToDate',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => <>{formatPrice(record.previousApplications + record.workInPlace + record.storedMaterials)}</>,
    },
    {
      title: t('screens:payAppClients.items.percentage'),
      key: 'percentage',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>
          {formatPercent(
            record.proposalOtherItem?.value > 0
              ? (record.previousApplications + record.workInPlace + record.storedMaterials) /
                  record.proposalOtherItem.value
              : 0
          )}
        </>
      ),
    },
    {
      title: t('screens:payAppClients.items.balanceToFinish'),
      key: 'balanceToFinish',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>
          {formatPrice(
            record.proposalOtherItem?.value -
              (record.previousApplications + record.workInPlace + record.storedMaterials)
          )}
        </>
      ),
    },
    {
      title: t('screens:payAppClients.items.retainage'),
      key: 'retainage',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>{formatPrice((record.previousApplications + record.workInPlace + record.storedMaterials) * 0.1)}</>
      ),
    },
    {
      title: t('screens:estimate.actions'),
      key: 'actions',
      align: 'center',
      width: '70px',
      render: record => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title={t('screens:estimate.editItem')}
          />
        </Space>
      ),
    },
  ];

  const tableColumnsSummary = [
    {
      title: t('screens:payAppClients.items.itemId'),
      dataIndex: 'id',
      key: 'id',
      ellipsis: { showTitle: false },
      width: '50px',
      align: 'center',
    },
    {
      title: t('screens:payAppClients.items.description'),
      key: 'proposalClientItem.description',
      ellipsis: { showTitle: false },
      align: 'center',
      width: '250px',
      render: record => (
        <>{(record.generalConditions && 'General Conditions') || (record.overheadAndProfit && 'Overhead and profit')}</>
      ),
    },
    {
      title: t('screens:payAppClients.items.scheduledValue'),
      dataIndex: 'proposalClientItem',
      key: 'proposalClientItem.total',
      ellipsis: { showTitle: false },
      align: 'center',
      render: proposalClientItem => <>{formatPrice(proposalClientItem?.total)}</>,
    },
    {
      title: t('screens:payAppClients.items.previousApplications'),
      dataIndex: 'previousApplications',
      key: 'previousApplications',
      ellipsis: { showTitle: false },
      align: 'center',
      render: previousApplications => <>{formatPrice(previousApplications)}</>,
    },
    {
      title: t('screens:payAppClients.items.workInPlace'),
      dataIndex: 'workInPlace',
      key: 'workInPlace',
      ellipsis: { showTitle: false },
      align: 'center',
      render: workInPlace => <>{formatPrice(workInPlace)}</>,
    },
    {
      title: t('screens:payAppClients.items.storedMaterials'),
      dataIndex: 'storedMaterials',
      key: 'storedMaterials',
      ellipsis: { showTitle: false },
      align: 'center',
      render: storedMaterials => <>{formatPrice(storedMaterials)}</>,
    },
    {
      title: t('screens:payAppClients.items.totalToDate'),
      key: 'totalToDate',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => <>{formatPrice(record.previousApplications + record.workInPlace + record.storedMaterials)}</>,
    },
    {
      title: t('screens:payAppClients.items.percentage'),
      key: 'percentage',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>
          {formatPercent(
            record.proposalClientItem?.total > 0
              ? (record.previousApplications + record.workInPlace + record.storedMaterials) /
                  record.proposalClientItem.total
              : 0
          )}
        </>
      ),
    },
    {
      title: t('screens:payAppClients.items.balanceToFinish'),
      key: 'balanceToFinish',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>
          {formatPrice(
            record.proposalClientItem?.total -
              (record.previousApplications + record.workInPlace + record.storedMaterials)
          )}
        </>
      ),
    },
    {
      title: t('screens:payAppClients.items.retainage'),
      key: 'retainage',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>{formatPrice((record.previousApplications + record.workInPlace + record.storedMaterials) * 0.1)}</>
      ),
    },
    {
      title: t('screens:estimate.actions'),
      key: 'actions',
      align: 'center',
      width: '70px',
      render: record => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title={t('screens:estimate.editItem')}
          />
        </Space>
      ),
    },
  ];

  const tableChangeOrderItems = [
    {
      title: t('screens:payAppClients.items.itemId'),
      dataIndex: 'id',
      key: 'id',
      ellipsis: { showTitle: false },
      width: '50px',
      align: 'center',
    },
    {
      title: t('screens:payAppClients.items.description'),
      dataIndex: 'changeOrderClient',
      key: 'changeOrderClient.title',
      ellipsis: { showTitle: false },
      align: 'center',
      width: '250px',
      render: changeOrderClient => {
        // list of items in change order
        const items = changeOrderClient.priceInfo.items;
        return (
          <>
            {changeOrderClient.title}
            <Popover
              content={items.map(item => (
                <div>{item.description}</div>
              ))}
            >
              <Button type="link" icon={<UnorderedListOutlined />} />
            </Popover>
          </>
        );
      },
    },
    {
      title: t('screens:payAppClients.items.scheduledValue'),
      key: 'value',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        const totalItemsValue = record.changeOrderClient.priceInfo.items.reduce(
          (acc, item) => acc + item.unitCost * item.quantity,
          0
        );
        return <>{formatPrice(totalItemsValue)}</>;
      },
    },
    {
      title: t('screens:payAppClients.items.previousApplications'),
      dataIndex: 'previousApplications',
      key: 'previousApplications',
      ellipsis: { showTitle: false },
      align: 'center',
      render: previousApplications => <>{formatPrice(previousApplications)}</>,
    },
    {
      title: t('screens:payAppClients.items.workInPlace'),
      dataIndex: 'workInPlace',
      key: 'workInPlace',
      ellipsis: { showTitle: false },
      align: 'center',
      render: workInPlace => <>{formatPrice(workInPlace)}</>,
    },
    {
      title: t('screens:payAppClients.items.storedMaterials'),
      dataIndex: 'storedMaterials',
      key: 'storedMaterials',
      ellipsis: { showTitle: false },
      align: 'center',
      render: storedMaterials => <>{formatPrice(storedMaterials)}</>,
    },
    {
      title: t('screens:payAppClients.items.totalToDate'),
      key: 'totalToDate',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => <>{formatPrice(record.previousApplications + record.workInPlace + record.storedMaterials)}</>,
    },
    {
      title: t('screens:payAppClients.items.percentage'),
      key: 'percentage',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        const totalItemsValue = record.changeOrderClient.priceInfo.items.reduce(
          (acc, item) => acc + item.unitCost * item.quantity,
          0
        );
        return formatPercent(
          totalItemsValue > 0
            ? (record.previousApplications + record.workInPlace + record.storedMaterials) / totalItemsValue
            : 0
        );
      },
    },
    {
      title: t('screens:payAppClients.items.balanceToFinish'),
      key: 'balanceToFinish',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => {
        const totalItemsValue = record.changeOrderClient.priceInfo.items.reduce(
          (acc, item) => acc + item.unitCost * item.quantity,
          0
        );
        return formatPrice(
          totalItemsValue - (record.previousApplications + record.workInPlace + record.storedMaterials)
        );
      },
    },
    {
      title: t('screens:payAppClients.items.retainage'),
      key: 'retainage',
      ellipsis: { showTitle: false },
      align: 'center',
      render: record => (
        <>{formatPrice((record.previousApplications + record.workInPlace + record.storedMaterials) * 0.1)}</>
      ),
    },
    {
      title: t('screens:estimate.actions'),
      key: 'actions',
      align: 'center',
      width: '70px',
      render: record => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title={t('screens:estimate.editItem')}
          />
        </Space>
      ),
    },
  ];

  return (
    visible && (
      <>
        <Formik
          enableReinitialize
          initialValues={initialFormValues}
          key={selectedRecord?.id ?? 'new'}
          onSubmit={handleSave}
          validateOnBlur={false}
          validateOnChange={false}
        >
          {({ errors, isSubmitting, touched, setFieldValue, values }) => {
            // console.log('Formik values:', values);
            // console.log('Formik touched:', touched);
            // console.log('Formik isSubmitting:', isSubmitting);
            // console.log('Erros do Formik:', errors);
            return (
              <>
                <Spin spinning={loading || isSubmitting}>
                  <Form disabled={viewOnly}>
                    <Row gutter={16}>
                      <Col span={24} style={{ paddingTop: '30px' }}>
                        <Descriptions title="PROJECT COST BREAKDOWN SHEET" layout="horizontal" column={1} />
                        <TotalsCalculator items={values.items} />
                        {/* {calculateTotals(values.items)} */}
                        <Table
                          locale={{ emptyText: 'No Items' }}
                          key="proposalClientItem"
                          rowKey="id"
                          columns={tableColumns}
                          dataSource={values.items.filter(
                            item =>
                              item.proposalClientItem &&
                              !item.generalConditions &&
                              !item.overheadAndProfit &&
                              !item.proposalOtherItem
                          )}
                          pagination={false}
                          bordered
                          size="small"
                          showExpandColumn={false}
                        />
                        {values.items.filter(item => item.proposalOtherItem).length > 0 && (
                          <Table
                            title={() => <Text>ADD ALT'S</Text>}
                            locale={{ emptyText: 'No Items' }}
                            key="proposalOtherItem"
                            rowKey="id"
                            showHeader={false}
                            columns={tableColumnsOtherItems}
                            dataSource={values.items.filter(item => item.proposalOtherItem)}
                            pagination={false}
                            bordered
                            size="small"
                            showExpandColumn={false}
                            style={{ marginTop: '-8px' }}
                          />
                        )}
                        {values.items.filter(item => item.generalConditions || item.overheadAndProfit).length > 0 && (
                          <Table
                            title={() => <Text>SUMMARY</Text>}
                            locale={{ emptyText: 'No Items' }}
                            key="proposalSummaryItems"
                            rowKey="id"
                            showHeader={false}
                            columns={tableColumnsSummary}
                            dataSource={values.items.filter(item => item.generalConditions || item.overheadAndProfit)}
                            pagination={false}
                            bordered
                            size="small"
                            showExpandColumn={false}
                            style={{ marginTop: '-8px' }}
                          />
                        )}
                        {values.items.filter(item => item.changeOrderClient).length > 0 && (
                          <Table
                            title={() => <Text>CHANGE ORDER</Text>}
                            locale={{ emptyText: 'No Change Orders Items' }}
                            key="ChangeOrderItems"
                            rowKey="id"
                            showHeader={false}
                            columns={tableChangeOrderItems}
                            dataSource={values.items.filter(item => item.changeOrderClient)}
                            pagination={false}
                            bordered
                            size="small"
                            showExpandColumn={false}
                            style={{ marginTop: '-8px' }}
                          />
                        )}
                        <div className="ant-table-wrapper" style={{ marginTop: '-8px' }}>
                          <div className="ant-table ant-table-middle ant-table-bordered ant-table-empty">
                            <div className="ant-table-container">
                              <div className="ant-table-content">
                                <table style={{ tableLayout: 'fixed' }} className="">
                                  <colgroup>
                                    <col style={{ width: '50px' }} />
                                    <col style={{ width: '250px' }} />
                                    <col />
                                    <col />
                                    <col />
                                    <col />
                                    <col />
                                    <col />
                                    <col />
                                    <col />
                                    <col style={{ width: '70px' }} />
                                  </colgroup>
                                  <tfoot className="ant-table-summary">
                                    <tr style={{ backgroundColor: '#fffff0' }}>
                                      <td className="ant-table-cell ant-table-cell-ellipsis" colSpan="2" />
                                      <td
                                        className="ant-table-cell ant-table-cell-ellipsis"
                                        style={{ textAlign: 'center' }}
                                      >
                                        <span className="ant-typography">
                                          <strong>{formatPrice(totalScheduleValue)}</strong>
                                        </span>
                                      </td>
                                      <td
                                        className="ant-table-cell ant-table-cell-ellipsis"
                                        style={{ textAlign: 'center' }}
                                      >
                                        <span className="ant-typography">
                                          <strong>{formatPrice(totalPreviousApplication)}</strong>
                                        </span>
                                      </td>
                                      <td
                                        className="ant-table-cell ant-table-cell-ellipsis"
                                        style={{ textAlign: 'center' }}
                                      >
                                        <span className="ant-typography">
                                          <strong>{formatPrice(totalWorkInPlace)}</strong>
                                        </span>
                                      </td>
                                      <td
                                        className="ant-table-cell ant-table-cell-ellipsis"
                                        style={{ textAlign: 'center' }}
                                      >
                                        <span className="ant-typography">
                                          <strong>{formatPrice(totalStoredMaterials)}</strong>
                                        </span>
                                      </td>
                                      <td
                                        className="ant-table-cell ant-table-cell-ellipsis"
                                        style={{ textAlign: 'center' }}
                                      >
                                        <span className="ant-typography">
                                          <strong>{formatPrice(totalToDate)}</strong>
                                        </span>
                                      </td>
                                      <td
                                        className="ant-table-cell ant-table-cell-ellipsis"
                                        style={{ textAlign: 'center' }}
                                      >
                                        <span className="ant-typography">
                                          <strong>{formatPercent(totalPercentageComplete)}</strong>
                                        </span>
                                      </td>
                                      <td
                                        className="ant-table-cell ant-table-cell-ellipsis"
                                        style={{ textAlign: 'center' }}
                                      >
                                        <span className="ant-typography">
                                          <strong>{formatPrice(totalBalanceToFinish)}</strong>
                                        </span>
                                      </td>
                                      <td
                                        className="ant-table-cell ant-table-cell-ellipsis"
                                        style={{ textAlign: 'center' }}
                                      >
                                        <span className="ant-typography">
                                          <strong>{formatPrice(totalRetainage)}</strong>
                                        </span>
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>
                    <Row style={{ width: '100%' }}>
                      <Col
                        span={24}
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          addingRight: 0,
                          paddingBottom: 20,
                          paddingTop: 20,
                        }}
                      >
                        <Button type="primary" htmlType="submit" loading={loading || isSubmitting}>
                          Save
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </Spin>
                <FormItem
                  selectedItem={selectedItem}
                  visible={showModal}
                  onClose={() => setShowModal(false)}
                  onUpdateItem={updateItemValues => {
                    const newItems = values.items.map(item => {
                      if (item.id === updateItemValues.id) {
                        return updateItemValues;
                      }
                      return item;
                    });
                    setFieldValue('items', newItems);
                    setShowModal(false);
                  }}
                />
              </>
            );
          }}
        </Formik>
      </>
    )
  );
}
